import { useEffect, useRef, useState } from "react";
import { useToast } from "@/components/Toast";

/** Click-to-edit text field.
 *
 * Saves on blur or Enter, cancels on Escape. Emits onSave(newValue) only when
 * the value actually changed, then the parent is responsible for the API call
 * + state refresh.
 */
export function InlineEdit({
  value,
  onSave,
  placeholder,
  multiline = false,
  className = "",
  inputClassName = "",
  ariaLabel,
}: {
  value: string;
  onSave: (v: string) => Promise<void> | void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  inputClassName?: string;
  ariaLabel?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      if (ref.current && "select" in ref.current) ref.current.select();
    }
  }, [editing]);

  async function commit() {
    const next = draft.trim();
    if (next === value) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave(next);
      setEditing(false);
      toast.success("Saved");
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
      setDraft(value); // revert
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    setDraft(value);
    setEditing(false);
  }

  if (!editing) {
    const isEmpty = !value || value.length === 0;
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={`text-left inline-edit ${isEmpty ? "inline-edit-empty" : ""} ${className}`}
        title="Click to edit"
        aria-label={ariaLabel ?? "Edit"}
      >
        {isEmpty ? placeholder ?? "—" : value}
      </button>
    );
  }

  const sharedProps = {
    value: draft,
    onChange: (e: any) => setDraft(e.target.value),
    onBlur: commit,
    onKeyDown: (e: any) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      } else if (!multiline && e.key === "Enter") {
        e.preventDefault();
        commit();
      } else if (multiline && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        commit();
      }
    },
    placeholder,
    disabled: busy,
    className: `inline-edit border-b-solid border-accent-500 ${inputClassName}`,
  };

  return multiline ? (
    <textarea
      ref={ref as any}
      {...sharedProps}
      rows={3}
      className={`input ${inputClassName}`}
    />
  ) : (
    <input ref={ref as any} {...sharedProps} />
  );
}
